import { Injectable } from '@angular/core';
import { EvaluationRule, MultipleChildResult, Result as ChildResult, TeacherResultsService } from '@isophi/pap-api';
import { BehaviorSubject, Observable, of, take } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { CategoryResultGroup } from '../models/category-result-group.model';
import { CategoryService } from './category.service';
import { ChildService } from './child.service';
import { AuthService } from '@isophi/core-legacy';

export interface ResultOption {
  label: string;
  value: string; // dataSetUuid
}

@Injectable({
  providedIn: 'root',
})
export class ResultService {
  selectedResult$: Observable<MultipleChildResult>;
  resultOptions$: Observable<ResultOption[]>;

  private selectedResultSubject$ = new BehaviorSubject<MultipleChildResult | null>(null);

  fetchAllTeacherResults$ = new BehaviorSubject<void>(undefined);

  // Incremented on every (re)load request; each loaded list is tagged with the request it belongs to,
  // so consumers can ignore the list replayed by shareReplay while a newer request is still in flight.
  private latestLoadId = 0;

  private loadedTeacherResults$ = this.fetchAllTeacherResults$.pipe(
    switchMap(() => {
      const loadId = ++this.latestLoadId;
      const user = this.authService.loggedUser;
      const results$ = user?.uuid
        ? this.teacherResultsService.teacherResultsList().pipe(
            map((res) => res.results),
            catchError(() => of([] as MultipleChildResult[]))
          )
        : of([] as MultipleChildResult[]);
      return results$.pipe(map((results) => ({ loadId, results })));
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  allTeacherResults$ = this.loadedTeacherResults$.pipe(map(({ results }) => results));

  constructor(
    private teacherResultsService: TeacherResultsService,
    private categoryService: CategoryService,
    private childService: ChildService,
    private authService: AuthService
  ) {
    this.selectedResult$ = this.selectedResultSubject$.asObservable();

    // Only the up-to-date list - a stale replay would make the selector pick an outdated result
    // (e.g. the previous fill right after the questionnaire was finished).
    this.resultOptions$ = this.loadedTeacherResults$.pipe(
      filter(({ loadId }) => loadId === this.latestLoadId),
      map(({ results }) =>
        results
          .sort((a, b) => new Date(b.finishedAt).getTime() - new Date(a.finishedAt).getTime())
          .map((result) => ({
            label: new Date(result.finishedAt).toLocaleDateString('cs-CZ', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }),
            value: result.dataSetUuid,
          }))
      )
    );
  }

  setSelectedResultById(id: string): void {
    this.allTeacherResults$
      .pipe(
        take(1),
        map((results) => results.find((r) => r.dataSetUuid === id) || null),
        tap((result) => this.selectedResultSubject$.next(result))
      )
      .subscribe();
  }

  getSelectedResult(): Observable<MultipleChildResult> {
    return this.selectedResult$;
  }

  getSelectedResultUuid(): string | null {
    return this.selectedResultSubject$.value?.dataSetUuid;
  }

  getSelectedResultCategoryGroups(): Observable<{ [evaluationCode: string]: CategoryResultGroup } | null> {
    return this.selectedResult$.pipe(
      switchMap((result) =>
        this.categoryService.getCategoryMap().pipe(
          map((categories) => {
            if (!(result && categories)) return null;
            return result.categoryResults.reduce(
              (groups, { categoryUuid, evaluationCode, evaluationBackgroundColor }) => ({
                ...groups,
                [evaluationCode]: {
                  evaluationCode,
                  evaluationBackgroundColor,
                  categories: [...(groups[evaluationCode]?.categories || []), categories[categoryUuid]],
                },
              }),
              {}
            );
          })
        )
      )
    );
  }

  getQuestionnaireCategoriesEvaluationCodes(): Observable<{ [uuid: string]: number } | null> {
    return this.childService.getSelectedChildResults().pipe(
      map((lastResults) =>
        lastResults?.questionnaire
          ? lastResults.questionnaire.categoryResults.reduce(
              (categoryEvaluationCodes, categoryResult) => ({
                ...categoryEvaluationCodes,
                [categoryResult.categoryUuid]: categoryResult.evaluationCode,
              }),
              {}
            )
          : null
      )
    );
  }

  getGeneralEvaluationRule(uuid: string): Observable<EvaluationRule> {
    return this.teacherResultsService.teacherResultsEvaluationGeneralRuleRetrieve(uuid);
  }

  getResultEvaluationRule(): Observable<EvaluationRule | null> {
    return this.selectedResult$.pipe(
      switchMap((result) => (result ? this.getGeneralEvaluationRule(result.dataSetUuid) : of(null))),
      tap((evaluationRule) => evaluationRule?.ranges?.sort((a, b) => b.code - a.code)),
      catchError((err) => {
        return of(null);
      })
    );
  }

  reloadAllTeacherResults(): void {
    this.fetchAllTeacherResults$.next();
  }

  /**
   * Forget the selected result, so the newest one gets selected once the results are loaded again
   * (e.g. after the teacher finishes the questionnaire).
   */
  resetSelection(): void {
    this.selectedResultSubject$.next(null);
  }

  clear(): void {
    this.selectedResultSubject$.next(null);
    this.fetchAllTeacherResults$.next();
  }
}
